import { useCallback, useState } from 'react';
import { lt, showSuccessAlert } from '@mews/framework';
import { Button, Dialog, DropDownButton, DropdownItem, Icon, IconName } from '@optimus-web/core';
import { useEnterpriseNavigate } from '@mews-commander/ui';
import { RoutingRuleDetailContainer } from '../../RoutingRuleDetailContainer/RoutingRuleDetailContainer';
import { RoutingRulesTableDataTestIds } from '../constants';
import { useDeleteRoutingRulesMutation } from '../../../mutations/useDeleteRoutingRulesMutation';

interface ActionCellProps {
    routingRuleId: string;
    routingRuleName: string | null | undefined;
}

export const ActionCell: React.FC<ActionCellProps> = ({ routingRuleId, routingRuleName }) => {
    const deleteMutation = useDeleteRoutingRulesMutation();
    const navigate = useEnterpriseNavigate();
    const onEdit = useCallback(() => {
        void navigate({
            to: 'RoutingRule/Detail/$routingRuleId',
            params: { routingRuleId },
        });
    }, [routingRuleId, navigate]);

    const [showDetail, setShowDetail] = useState(false);
    const [showConfirmDelete, setShowConfirmDelete] = useState(false);

    const hideModal = () => setShowConfirmDelete(false);

    const deleteRoutingRule = () => {
        deleteMutation.mutate(
            { routingRuleIds: [routingRuleId] },
            {
                onSuccess: () => {
                    setShowConfirmDelete(false);
                    showSuccessAlert({
                        description: lt('DeleteSuccessMessage', { entity: routingRuleName ?? '' }),
                    });
                },
            }
        );
    };

    return (
        <>
            <DropDownButton icon={IconName.More} size="small" variant="ghost">
                <DropdownItem
                    data-test-id={RoutingRulesTableDataTestIds.OptionViewDetail}
                    label={lt('ViewDetails')}
                    leftDetail={<Icon value={IconName.ReservationOverview} size="small" />}
                    onClick={() => setShowDetail(true)}
                />
                <DropdownItem
                    data-test-id={RoutingRulesTableDataTestIds.OptionEdit}
                    label={lt('Edit')}
                    leftDetail={<Icon value={IconName.Edit} size="small" />}
                    onClick={onEdit}
                />
                <DropdownItem
                    data-test-id={RoutingRulesTableDataTestIds.OptionDelete}
                    label={lt('Delete')}
                    leftDetail={<Icon value={IconName.Delete} size="small" />}
                    onClick={() => setShowConfirmDelete(true)}
                />
            </DropDownButton>
            <Dialog
                loading={deleteMutation.isPending}
                active={showConfirmDelete}
                title={lt('RoutingRulesDeleteConfirmationTitle')}
                size="small"
                onClose={hideModal}
                footer={{
                    primary: (
                        <Button variant="destructive" onClick={deleteRoutingRule} icon={IconName.Delete} type="submit">
                            {lt('Delete')}
                        </Button>
                    ),
                    secondary: (
                        <Button variant="tertiary" onClick={hideModal} type="submit">
                            {lt('GoBack')}
                        </Button>
                    ),
                }}
            >
                {lt('RoutingRulesDeleteConfirmationDescription')}
            </Dialog>
            <RoutingRuleDetailContainer
                opened={showDetail}
                routingRuleId={routingRuleId}
                onClose={() => setShowDetail(false)}
                aggregateItems
            />
        </>
    );
};
